import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Build an animated pure SVG dynamic height accordion with React and D3",
  "description": "",
  "published": "2018-06-01T00:00:00.000Z",
  "redirect_from": ["/blog/build-animated-accordion-react-d3/swizec/8418"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <iframe {...{
      "src": "https://codesandbox.io/embed/r02j7p45w4",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`This is a proof of concept I built for a client. They were building an event flow data visualization and weren't happy with how finicky and hard to use their components turned out.`}</p>
    <p>{`So they asked me for help 💪`}</p>
    <h2 {...{
      "id": "goal"
    }}>{`Goal`}</h2>
    <p>{`The goal was to have an event flow visualization where icons represent different types of events, are connected with an unbroken line, and show additional information when clicked. The additional information can contain sub-flows, further data visualization, or just text.`}</p>
    <p>{`It’s inspired by how GitHub visualizes the PR process but is intended for richer graphics. Hence why it has to be in SVG.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/06/githubscreenshot.jpg",
        "alt": "GitHub shows the PR process using connected event icons with descriptions"
      }}></img></p>
    <h2 {...{
      "id": "problems-to-solve--gotchas-to-catch"
    }}>{`Problems to solve / gotchas to catch`}</h2>
    <p>{`The trouble begins when you realize SVG is `}<em parentName="p">{`terrible`}</em>{` for layout. You want SVG because it's great for dataviz –fun shapes, vector graphics, full control of element positioning. Great.`}</p>
    <p>{`But also full control of element positioning. 😩`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Surely can’t be harder than text layiut in SVG`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/999619898555826176"
      }}>{`May 24, 2018`}</a></blockquote>
    <p>{`With great power comes great amounts of work, you see. Where HTML performs basic layouting for you, SVG does not. Want text to flow into a new line? Do it yourself. Want elements to push other elements out of the way? Do it yourself. Want anything to happen? Do it yourself.`}</p>
    <p>{`This is great when you have all the information up front.`}</p>
    <p>{`But it’s terrible when you can't know the size of some elements. Like the height of those dynamic blocks on the right.`}</p>
    <p>{`My client tried several approaches and finally settled on a component structure a little like this 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/06/component-structure1.jpg",
        "alt": "Almost good enough structure"
      }}></img></p>
    <p>{`Each row is a `}<inlineCode parentName="p">{`div`}</inlineCode>{` that contains an `}<inlineCode parentName="p">{`svg`}</inlineCode>{` on the left and a `}<inlineCode parentName="p">{`div`}</inlineCode>{` on the right. The SVG renders our icon and the vertical line. The `}<inlineCode parentName="p">{`div`}</inlineCode>{` contains potentially expanding descriptions.`}</p>
    <p>{`When the inner `}<inlineCode parentName="p">{`div`}</inlineCode>{` becomes bigger, it resizes the container `}<inlineCode parentName="p">{`div`}</inlineCode>{`. This pushes the rows below further down.`}</p>
    <p>{`All great 👌`}</p>
    <p>{`But it's finicky, difficult to align, and you don't even wanna know what happens when someone resizes their browser and elements start breaking into new lines.`}</p>
    <p>{`The objective, therefore, is to build a solution that:`}</p>
    <ul>
      <li parentName="ul">{`is not finicky`}</li>
      <li parentName="ul">{`has a simple API`}</li>
      <li parentName="ul">{`works with arbitrary row heights`}</li>
      <li parentName="ul">{`can handle rows resizing after initial render`}</li>
      <li parentName="ul">{`allows users to render anything into this structure`}</li>
    </ul>
    <h2 {...{
      "id": "the-solution"
    }}>{`The solution`}</h2>
    <p>{`Render everything inside an SVG, use `}<inlineCode parentName="p">{`foreignObject`}</inlineCode>{` to support auto text layouting on the right, and abuse React ref callbacks to deal with dynamic height.`}</p>
    <p>{`In a nutshell 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/06/accordionflow-sketch.jpg",
        "alt": null
      }}></img></p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`<AccordionFlow>`}</inlineCode>{` renders rows`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`<AccordionFlow>`}</inlineCode>{` holds an array of known or default row heights, used for vertical positioning`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`<Row>`}</inlineCode>{` gets 2 render props, `}<inlineCode parentName="li">{`icon`}</inlineCode>{` and `}<inlineCode parentName="li">{`content`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`icon`}</inlineCode>{` renders the left side`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content`}</inlineCode>{` renders the right side inside a `}<inlineCode parentName="li">{`<foreignObject>`}</inlineCode></li>
      <li parentName="ul">{`a ref callback on `}<inlineCode parentName="li">{`content`}</inlineCode>{` triggers a height update callback`}</li>
      <li parentName="ul">{`the height update callback updates a list of heights in `}<inlineCode parentName="li">{`<AccordionFlow>`}</inlineCode></li>
      <li parentName="ul">{`this triggers a re-render and each `}<inlineCode parentName="li">{`<Row>`}</inlineCode>{` declaratively transitions itself into its new position`}</li>
      <li parentName="ul">{`when the `}<inlineCode parentName="li">{`content`}</inlineCode>{` updates itself, it calls a callback that tells `}<inlineCode parentName="li">{`<Row>`}</inlineCode>{` its height has changed`}</li>
      <li parentName="ul">{`the same reflow happens as before`}</li>
    </ul>
    <p>{`Savvy? Here's how it works again`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/r02j7p45w4",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`Let's look at the code :)`}</p>
    <h2 {...{
      "id": "indexjs"
    }}>{`index.js`}</h2>
    <p>{`You can think of this as the consumer side. Whomever needs to render our `}<inlineCode parentName="p">{`AccordionFlow`}</inlineCode>{`.`}</p>
    <p>{`It starts by creating an array of arrays to represent our data. Each has an `}<inlineCode parentName="p">{`<Icon>`}</inlineCode>{` and a `}<inlineCode parentName="p">{`<Content>`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// index.js prep data

const icons = [<circle>, <rectangle>, <triangle>],
  flowData = d3range(10).map(i => [
    icons[i % 3],
    contentUpdated => (
      <dynamiccontent title="{\`Row" \${i}\`}="" contentupdated={contentUpdated}>
        {d3range(10)
          .slice(0, i)
          .map(() => faker.lorem.paragraph())}
      </dynamiccontent>
    )
  ]);
</triangle></rectangle></circle>
`}</code></pre>
    <p>{`Icon is a plain component. We're rendering it as a compound component with no frills. \`\`is wrapped in a function because it's going to be used as a render prop. It gets the callback function it can call to dynamically update its height after initial rendering.`}</p>
    <p>{`We need this so `}<inlineCode parentName="p">{`AccordionFlow`}</inlineCode>{` can know when to push other rows out of the way.`}</p>
    <p>{`Rendering our dataviz is meant to be simple 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// index.js render method
<svg width="600" height="2240">
  <accordionflow data={flowData}></accordionflow>
</svg>
`}</code></pre>
    <p>{`Svg is very tall to make room for expanding. We `}<em parentName="p">{`could`}</em>{` wrap `}<inlineCode parentName="p">{`AccordionFlow`}</inlineCode>{` in additional callbacks and make svg height dynamic, but this is a proof of concept :)`}</p>
    <h2 {...{
      "id": ""
    }}></h2>
    <p>{`AccordionFlow is where we render every row, the vertical line on the left, keep a list of known heights for each row, and handle vertical positioning.`}</p>
    <p>{`Using idiomatic but not the most readable code, it comes out to 30 lines. 💁‍♀️`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// AccordionFlow

class AccordionFlow extends React.Component {
  defaultHeight = 50;
  state = {
    heights: this.props.data.map(_ => this.defaultHeight)
  };
  render() {
    const { data } = this.props,
      { heights } = this.state;

    return (
      <g transform="translate(0, 20)">
        <line x1={15} x2={15} y1={10} y2="{heights.reduce((sum," h)=""> sum + h, 0)}
          stroke="lightgrey"
          strokeWidth="2.5"
        />
        {data.map(([icon, content], i) => (
          <row icon={icon} content={content} y="{heights.slice(0," i).reduce((sum,="" h)=""> sum + h, 0)}
            width={450}
            key={i}
            reportHeight={height => {
              let tmp = [...heights];
              tmp[i] =
                height !== undefined && height > this.defaultHeight
                  ? height
                  : this.defaultHeight;
              this.setState({ heights: tmp });
            }}
          />
        ))}
      </row></line></g>
    );
  }
}
`}</code></pre>
    <p>{`We start with default `}<inlineCode parentName="p">{`heights`}</inlineCode>{` of `}<inlineCode parentName="p">{`50`}</inlineCode>{` pixels, and render a grouping element `}<inlineCode parentName="p">{`<g>`}</inlineCode>{` to help with positioning. Inside we render a vertical `}<inlineCode parentName="p">{`<line>`}</inlineCode>{` to connect all icons, then go into a loop of rows.`}</p>
    <p>{`Each `}<inlineCode parentName="p">{`<Row>`}</inlineCode>{` gets`}</p>
    <ul>
      <li parentName="ul">{`an `}<inlineCode parentName="li">{`icon`}</inlineCode>{` render prop`}</li>
      <li parentName="ul">{`a `}<inlineCode parentName="li">{`content`}</inlineCode>{` render prop`}</li>
      <li parentName="ul">{`vertical position `}<inlineCode parentName="li">{`y`}</inlineCode>{` calculated as the sum of all `}<inlineCode parentName="li">{`heights`}</inlineCode>{` so far`}</li>
      <li parentName="ul">{`a `}<inlineCode parentName="li">{`width`}</inlineCode>{`, which helps it figure out its height`}</li>
      <li parentName="ul">{`a `}<inlineCode parentName="li">{`key`}</inlineCode>{` just so React doesn't complain`}</li>
      <li parentName="ul">{`a `}<inlineCode parentName="li">{`reportHeight`}</inlineCode>{` callback, which updates a particular height in our `}<inlineCode parentName="li">{`heights`}</inlineCode>{` array`}</li>
    </ul>
    <p>{`We should probably move that callback into a class method, but we need to encapsulate the index. Best we could do is something like `}<inlineCode parentName="p">{`height => this.reportHeight(height, i)`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "-1"
    }}></h2>
    <p>{`The \`\`component is more beastly. It renders the icon and the content, handles height callbacks, and uses my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/declarative-d3-transitions-react/swizec/8323"
      }}>{`declarative D3 transitions with React 16.3+`}</a>{` approach to animate its vertical positioning.`}</p>
    <p>{`64 lines of code in total.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Row extends React.Component {
  state = {
    open: false,
    y: this.props.y,
  };

  toggleOpen = () => this.setState({ open: !this.state.open });

  // needed for animation
  rowRef = React.createRef();

  // magic dynamic height detection
  contentRefCallback = (element) => {
    if (element) {
      this.contentRef = element;
      this.props.reportHeight(element.getBoundingClientRect().height);
    } else {
      this.props.reportHeight();
    }
  };

  contentUpdated = () => {
    this.props.reportHeight(this.contentRef.getBoundingClientRect().height);
  };

  componentDidUpdate() {
    // handle animation
  }

  render() {
    // render stuff
  }
}
`}</code></pre>
    <p>{`Our \``}<inlineCode parentName="p">{`component uses `}</inlineCode>{`state`}<inlineCode parentName="p">{`to keep track of whether it's`}</inlineCode>{`open`}<inlineCode parentName="p">{`and its vertical position`}</inlineCode>{`y\`.`}</p>
    <p>{`We use `}<inlineCode parentName="p">{`toggleOpen`}</inlineCode>{` to flip the `}<inlineCode parentName="p">{`open`}</inlineCode>{` switch. Right now, that just means the difference between `}<inlineCode parentName="p">{`content`}</inlineCode>{` being rendered or not.`}</p>
    <p>{`The fun stuff happens in `}<inlineCode parentName="p">{`contentRefCallback`}</inlineCode>{`. We use this as a `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/refs-and-the-dom.html#callback-refs"
      }}>{`React ref callback`}</a>{`, which is a method that React calls when a new element is rendered into the DOM.`}</p>
    <p>{`We use this opportunity to save the ref as a component property for future reference, get its height using `}<inlineCode parentName="p">{`getBoundingClientRect`}</inlineCode>{`, and call the `}<inlineCode parentName="p">{`reportHeight`}</inlineCode>{` callback to tell \`\`about our new height.`}</p>
    <p>{`Something similar happens in `}<inlineCode parentName="p">{`contentUpdated`}</inlineCode>{`. It's a callback we pass into the `}<inlineCode parentName="p">{`content`}</inlineCode>{` render prop so it can tell us when something changes. We then re-check our new height and report it up the hierarchy.`}</p>
    <h3 {...{
      "id": "render"
    }}>{`render`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`render`}</inlineCode>{` method puts all of this together.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  render() {
    const { icon, content, width } = this.props,
      { y } = this.state;

    return (
      <g transform="{\`translate(5," \${y})\`}="" ref={this.rowRef}>
        <g onclick={this.toggleOpen} style={{ cursor: "pointer" }}>
          {icon}
        </g>
        {this.state.open ? (
          <foreignobject x={20} y={-20} width={width} style={{ border: "1px solid red" }}>
            <div ref={this.contentRefCallback}>
              {typeof content === "function"
                ? content(this.contentUpdated)
                : content}
            </div>
          </foreignobject>
        ) : null}
      </g>
    );
  }
`}</code></pre>
    <p>{`We start with a grouping element \``}<inlineCode parentName="p">{`that handles positioning and sets the `}</inlineCode>{`rowRef\`.`}</p>
    <p>{`Inside, we render first a grouping element with a click callback and our `}<inlineCode parentName="p">{`icon`}</inlineCode>{`. This lets us open and close a row.`}</p>
    <p>{`Followed by a conditional rendering of a `}<inlineCode parentName="p">{`foreignObject`}</inlineCode>{` that contains our `}<inlineCode parentName="p">{`content`}</inlineCode>{`. Foreign objects are SVG elements that let you render just about anything. HTML, more SVG, .... HTML. Mostly HTML, that's the magic.`}</p>
    <p>{`This `}<inlineCode parentName="p">{`foreignObject`}</inlineCode>{` has an `}<inlineCode parentName="p">{`x,y`}</inlineCode>{` position to compensate for some funniness, and a `}<inlineCode parentName="p">{`width`}</inlineCode>{` that helps your browser's layouting engine decide what to do.`}</p>
    <p>{`It then contains a `}<inlineCode parentName="p">{`div`}</inlineCode>{` so we can attach our `}<inlineCode parentName="p">{`contentRefCallback`}</inlineCode>{` because putting it on `}<inlineCode parentName="p">{`foreignObject`}</inlineCode>{` always reported height as `}<inlineCode parentName="p">{`0`}</inlineCode>{`. I don't know why.`}</p>
    <p>{`We then render our `}<inlineCode parentName="p">{`content`}</inlineCode>{` as either a functional render prop being passed the `}<inlineCode parentName="p">{`contentUpdated`}</inlineCode>{` callback, or a simple component.`}</p>
    <h3 {...{
      "id": "componentdidupdate"
    }}>{`componentDidUpdate`}</h3>
    <p><inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{` declaratively handles the vertical transitioning of every row's vertical position. You should read my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/declarative-d3-transitions-react/swizec/8323"
      }}>{`Declarative D3 transitions with React 16.3`}</a>{` article for details.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  componentDidUpdate() {
    const { y } = this.props;
    d3
      .select(this.rowRef.current)
      .transition()
      .duration(500)
      .ease(d3.easeCubicInOut)
      .attr("transform", \`translate(5, \${y})\`)
      .on("end", () => {
        this.setState({
          y
        });
      });
  }
`}</code></pre>
    <p>{`The idea is that we take the new `}<inlineCode parentName="p">{`y`}</inlineCode>{` prop, transition our DOM node directly using D3, then update our `}<inlineCode parentName="p">{`state`}</inlineCode>{` to keep React in sync with reality.`}</p>
    <h2 {...{
      "id": "-2"
    }}></h2>
    <p>{`To be honest, `}<inlineCode parentName="p">{`<DynamicContent>`}</inlineCode>{` isn't the fun part of this code. I include it here for completeness.`}</p>
    <p>{`It's a component that renders a title and some paragraphs, waits 1.2 seconds, then adds another paragraph. There's also a spinner to make things more interesting.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`lass DynamicContent extends React.Component {
  state = {
    paragraphs: this.props.children,
    spinner: true
  };
  componentDidMount() {
    setTimeout(() => {
      this.setState({
        paragraphs: [...this.state.paragraphs, faker.lorem.paragraph()],
        spinner: false
      });
      this.props.contentUpdated();
    }, 1200);
  }

  render() {
    const { title } = this.props,
      { paragraphs, spinner } = this.state;

    return (
      <react class="fragment">
        <h3>{title}</h3>
        {paragraphs.map(c => {c}

)}
        <p>
          {spinner && (
            <spinner name="cube-grid" color="green" fadein="none" style={{ margin: "0 auto" }}>
          )}


    );
  }
}
</spinner></p></react>
`}</code></pre>
    <p>{`See, state holds paragraphs and a spinner flag. `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{` has a timeout of 1.2 seconds, then adds another paragraph and calls the `}<inlineCode parentName="p">{`contentUpdated`}</inlineCode>{` callback.`}</p>
    <p>{`The render method returns a `}<inlineCode parentName="p">{`React.Fragment`}</inlineCode>{` containing an `}<inlineCode parentName="p">{`h3`}</inlineCode>{` with the `}<inlineCode parentName="p">{`title`}</inlineCode>{`, a bunch of paragraphs, and an optional `}<inlineCode parentName="p">{`Spinner`}</inlineCode>{` from `}<a parentName="p" {...{
        "href": "https://github.com/KyleAMathews/react-spinkit"
      }}>{`react-spinkit`}</a>{`.`}</p>
    <p>{`Nothing special, but the dynamic stuff could wreak havoc with our `}<inlineCode parentName="p">{`AccordionFlow`}</inlineCode>{` without that callback.`}</p>
    <p>{`Happy hacking 🤓`}</p>
    <h2 {...{
      "id": "should-i-open-source-this"
    }}>{`Should I open source this?`}</h2>
    <p>{`What do you think, should I open source this? Would anyone find it useful? Ping me on Twitter or Reddit since I'm prob posting this to Reddit 😛`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`📖 Boom, new in-depth article It's been a while 😅`}<br parentName="p"></br><br parentName="p"></br>{`Build an animated pure SVG dynamic height accordion dataviz with `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/React?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#React`}</a>{` and `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/D3?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#D3`}</a>{` 📊`}<br parentName="p"></br><br parentName="p"></br>{`Should I open source this? 🤔 Designed to be flexible and easy to use`}<br parentName="p"></br><br parentName="p"></br>{`👉 `}<a parentName="p" {...{
          "href": "https://t.co/AlfBxCGzkf"
        }}>{`https://t.co/AlfBxCGzkf`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/Azyvx3tsj0"
        }}>{`pic.twitter.com/Azyvx3tsj0`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1002602899845738496"
      }}>{`June 1, 2018`}</a></blockquote>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      